import React, {useCallback, useMemo, useState} from 'react';
import {Box, TextField, Typography} from '@mui/material';
import axios from 'axios';
import {toast} from 'react-toastify';
import base_url from '../../../baseUrls';
import {COMPANY_ELEMENTS, companyRules} from '../../../permissions/companyAccessControl';
import {NewPermissionGate} from '../../../permissions/permissionGate';
import MyPaginatedGrid from '../../../pieces/grids/paginatedGridWrapper';
import ColumnDefinition from '../../../pieces/grids/columnGenerator';
import DisplayListsPageWrapper from '../../wrappers/displayListsPageWrapper';
import ListPageHeader from '../../../pieces/listPageHeader';
import {ListViewHeader} from '../../jobs/components/listViewHeader';
import usePagination from '../../../hooks/usePagination';
import {useGridViewState} from '../../../hooks/useGridViewState';
import AccessDenied from '../../misc/accessDenied';
import {globalUseClasses} from '../../../styles/globalClasses';
import Utils from '../../../utils/utils';
import ApiUtils from '../../../utils/apiUtils';
import ModalWrapper, {ModalContentWrapper} from '../../../pieces/modals/modalWrapper';
import FormSubmitButtons from '../../../pieces/formSubmitButtons';

const NOTE_FIELDS = {
  ID: {api_name: 'id', label: 'Id'},
  DESCRIPTION: {api_name: 'noteDescription', label: 'Note'},
  CREATED_AT: {api_name: 'createdAt', label: 'Created At'},
  CREATED_BY: {api_name: 'createdBy', label: 'Created By'},
  UPDATED_AT: {api_name: 'updatedAt', label: 'Updated At'},
};

const getApiPayload = (response) => {
  const data = response?.data;
  if (!data) return null;
  return data.data ?? data.note ?? data.internalNote ?? data;
};

const formatDate = (value) => {
  if (!value) return '';
  try {
    return Utils.formatIsoDateString(value, {date: true, time: true});
  } catch (e) {
    return value;
  }
};

const getNoteId = (row) => {
  return row?.noteId || row?._id || row?.id || '';
};

export const CompanyNotesInfo = () => {
  const viewController = useGridViewState();
  const classes = globalUseClasses();
  const pagination = usePagination();
  const companyId = Utils.getUrlParameter('companyId') ?? '';

  const canAccessNotes = NewPermissionGate({
    user: viewController.user,
    elementPermissions: companyRules[COMPANY_ELEMENTS.notesInfo]
  });

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState('');
  const [noteDescription, setNoteDescription] = useState('');
  const [selectedNote, setSelectedNote] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const openCreateModal = useCallback(() => {
    setEditingNoteId('');
    setSelectedNote(null);
    setNoteDescription('');
    setFormModalOpen(true);
  }, []);

  const closeFormModal = useCallback(() => {
    if (submitting) return;
    setFormModalOpen(false);
    setEditingNoteId('');
    setSelectedNote(null);
    setNoteDescription('');
  }, [submitting]);

  const closeDeleteModal = useCallback(() => {
    if (submitting) return;
    setDeleteModalOpen(false);
    setSelectedNote(null);
  }, [submitting]);

  const authHeader = async () => {
    const token = await ApiUtils.getAccessToken();
    if (!token) return null;
    return {Authorization: token};
  };

  const fetchOneNote = useCallback(async (noteId) => {
    const headers = await authHeader();
    if (!headers || !companyId || !noteId) return null;
    const url = `${base_url.api}companies/${companyId}/internal-notes/${noteId}`;
    const response = await axios.get(url, {headers});
    return getApiPayload(response);
  }, [companyId]);

  const openEditModal = useCallback(async (row) => {
    const noteId = getNoteId(row);
    if (!noteId) {
      toast.error('Unable to identify the selected note.', {toastId: 'note_edit_no_id'});
      return;
    }
    setSubmitting(true);
    try {
      const note = await fetchOneNote(noteId);
      setEditingNoteId(noteId);
      setSelectedNote(note || row);
      setNoteDescription(note?.noteDescription || row?.noteDescription || '');
      setFormModalOpen(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load note details.', {toastId: 'note_fetch_by_id_error'});
    } finally {
      setSubmitting(false);
    }
  }, [fetchOneNote]);

  const openDeleteModal = useCallback((row) => {
    setSelectedNote(row);
    setDeleteModalOpen(true);
  }, []);

  const refreshGridAndClose = () => {
    viewController.refreshGrid();
    setFormModalOpen(false);
    setDeleteModalOpen(false);
    setEditingNoteId('');
    setSelectedNote(null);
    setNoteDescription('');
  };

  const submitNote = useCallback(async () => {
    const trimmedDescription = (noteDescription || '').trim();
    if (!trimmedDescription) {
      toast.error('Note description is required.', {toastId: 'note_required'});
      return;
    }
    if (!companyId) {
      toast.error('Company ID was not found.', {toastId: 'note_company_missing'});
      return;
    }

    const headers = await authHeader();
    if (!headers) return;

    const isEditing = Boolean(editingNoteId);
    const payload = isEditing
      ? {
        companyId,
        noteId: editingNoteId,
        noteDescription: trimmedDescription,
      }
      : {
        companyId,
        noteDescription: trimmedDescription,
      };

    setSubmitting(true);
    try {
      if (isEditing) {
        await axios.put(`${base_url.api}companies/${companyId}/internal-notes/${editingNoteId}`, payload, {headers});
      } else {
        await axios.post(`${base_url.api}companies/${companyId}/internal-notes`, payload, {headers});
      }

      toast.success(isEditing ? 'Note updated successfully.' : 'Note created successfully.', {
        toastId: isEditing ? 'note_update_success' : 'note_create_success'
      });
      refreshGridAndClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || (isEditing ? 'Failed to update note.' : 'Failed to create note.'), {
        toastId: isEditing ? 'note_update_error' : 'note_create_error'
      });
    } finally {
      setSubmitting(false);
    }
  }, [companyId, editingNoteId, noteDescription, viewController]);

  const deleteNote = useCallback(async () => {
    const noteId = getNoteId(selectedNote);
    if (!companyId || !noteId) {
      toast.error('Unable to delete note. Missing note id.', {toastId: 'note_delete_no_id'});
      return;
    }
    const headers = await authHeader();
    if (!headers) return;

    setSubmitting(true);
    try {
      await axios.delete(`${base_url.api}companies/${companyId}/internal-notes/${noteId}`, {headers});
      toast.success('Note deleted successfully.', {toastId: 'note_delete_success'});
      refreshGridAndClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete note.', {toastId: 'note_delete_error'});
    } finally {
      setSubmitting(false);
    }
  }, [companyId, selectedNote, viewController]);

  const getContextMenuItems = useCallback((params) => {
    if (!params?.node) return [];
    const row = params.node.data;
    const disableForMultiple = viewController.selectedMultipleRows?.() ?? false;
    return [
      {
        name: 'Edit',
        disabled: disableForMultiple,
        cssClasses: ['bold'],
        action: () => openEditModal(row),
      },
      {
        name: 'Delete',
        disabled: disableForMultiple,
        action: () => openDeleteModal(row),
      },
      'separator',
      {name: 'Export Selected (.xlsx)', action: () => params.api.exportDataAsExcel({onlySelected: true})},
      'copy',
    ];
  }, [openDeleteModal, openEditModal, viewController]);

  const columnDefs = useMemo(() => {
    return [
      new ColumnDefinition({field: NOTE_FIELDS.ID, hide: true}),
      new ColumnDefinition({
        field: NOTE_FIELDS.DESCRIPTION,
        width: 560,
        sortable: true,
        wrapText: true,
        cellStyle: {lineHeight: 1.75, wordBreak: 'break-word'},
      }),
      new ColumnDefinition({
        field: NOTE_FIELDS.CREATED_BY,
        width: 190,
        valueGetter: ({data}) => {
          const createdBy = data?.[NOTE_FIELDS.CREATED_BY.api_name];
          if (!createdBy || typeof createdBy !== 'object') return '';
          return [createdBy.firstName, createdBy.lastName].filter(Boolean).join(' ').trim();
        },
        sortable: true,
      }),
      new ColumnDefinition({
        field: NOTE_FIELDS.CREATED_AT,
        width: 190,
        filter: 'agDateColumnFilter',
        valueGetter: ({data}) => formatDate(data?.[NOTE_FIELDS.CREATED_AT.api_name]),
        sortable: true,
      }),
      new ColumnDefinition({
        field: NOTE_FIELDS.UPDATED_AT,
        width: 190,
        filter: 'agDateColumnFilter',
        valueGetter: ({data}) => formatDate(data?.[NOTE_FIELDS.UPDATED_AT.api_name]),
        sortable: true,
      }),
    ];
  }, []);

  const fetchParams = useMemo(() => {
    if (!companyId) return null;
    return {
      url: `${base_url.api}companies/${companyId}/internal-notes`,
      respKey: 'internalNotes',
    };
  }, [companyId]);

  if (!canAccessNotes) {
    return <AccessDenied />;
  }

  const headerComponent = (
    <ListPageHeader
      viewController={viewController}
      header=""
      addHandler={openCreateModal}
      showAddButton={true}
      headComponent={<ListViewHeader title={`Notes${pagination.totalItemsCount != null ? ` (${pagination.totalItemsCount})` : ''}`} viewController={viewController} pagination={pagination} />}
    >
      {pagination.ItemsController}
    </ListPageHeader>
  );

  const content = (
    <Box className={classes.pageInsideTab}>
      <DisplayListsPageWrapper headerComponent={headerComponent}>
        {!companyId && (
          <Box sx={{p: 2, color: 'text.secondary'}}>
            Company ID was not found.
          </Box>
        )}
        {companyId && fetchParams && (
          <>
            <MyPaginatedGrid
              columnDefs={columnDefs}
              ls_name="company_notes_grid"
              registerGrid={viewController.setGridToParent}
              registerTempGrid={viewController.setTempGridToParent}
              fetchParams={fetchParams}
              companyId={companyId}
              onFetchFail={viewController.onFetchFail}
              pagination={pagination}
              refreshTimestamp={viewController.refreshTimestamp}
              isTabOffset={true}
              contextMenu={getContextMenuItems}
            />
            {pagination.PageController}
          </>
        )}
      </DisplayListsPageWrapper>

      {formModalOpen && (
        <ModalWrapper
          open={formModalOpen}
          handleClose={closeFormModal}
          modalName={editingNoteId ? 'edit-company-note' : 'add-company-note'}
          width={{xs: '98%', sm: '700px'}}
          preventScroll={false}
        >
          <ModalContentWrapper
            handleClose={closeFormModal}
            header={editingNoteId ? 'Update Note' : 'Add Note'}
          >
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
              <Typography variant="subtitle2" sx={{mb: -1}}>
                Note Description
              </Typography>
              <TextField
                value={noteDescription}
                onChange={(e) => setNoteDescription(e.target.value)}
                multiline
                minRows={4}
                fullWidth
                disabled={submitting}
              />
              <FormSubmitButtons
                cancelText="Cancel"
                submitText={editingNoteId ? 'Update' : 'Add'}
                cancelAction={closeFormModal}
                submitAction={submitNote}
                disableSubmit={submitting}
                disableCancel={submitting}
              />
            </Box>
          </ModalContentWrapper>
        </ModalWrapper>
      )}

      {deleteModalOpen && (
        <ModalWrapper
          open={deleteModalOpen}
          handleClose={closeDeleteModal}
          modalName="delete-company-note"
          width={{xs: '98%', sm: '560px'}}
          preventScroll={false}
        >
          <ModalContentWrapper handleClose={closeDeleteModal} header="Delete Note">
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
              <Typography>
                Are you sure you want to delete this note?
              </Typography>
              <Typography color="text.secondary">
                {(selectedNote?.noteDescription || '').trim() || 'No description'}
              </Typography>
              <FormSubmitButtons
                cancelText="No"
                submitText="Yes, Delete"
                cancelAction={closeDeleteModal}
                submitAction={deleteNote}
                disableSubmit={submitting}
                disableCancel={submitting}
              />
            </Box>
          </ModalContentWrapper>
        </ModalWrapper>
      )}
    </Box>
  );

  return viewController.RenderElement(content);
};

export default CompanyNotesInfo;
